/**
 * NiV "התקדמות לפי ימים" — system-wide progress-by-day charts.
 *
 * Data: niv_system_daily (one numbers-only doc per day, written by the nightly
 * telemetry function) + the existing niv_telemetry_daily overview series for
 * active therapists / clinics. Small-multiples grid, 30 / 90 day range.
 *
 * Usage figures (SMS / AI / video / signatures) are day-over-day deltas of the
 * cumulative monthlyUsage docs, so:
 *  - the series starts on the first night after deploy (no back-fill),
 *  - a missed night is absorbed into the next day's bar.
 */

import { useMemo, useState } from "react";
import {
  ComposedChart,
  Area,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import {
  ChatText,
  Sparkle,
  Robot,
  VideoCamera,
  UsersThree,
  UserPlus,
  CalendarCheck,
  NotePencil,
  ChartLineUp,
  Buildings,
  CurrencyDollar,
  Signature,
  Info,
  TrendUp,
} from "@phosphor-icons/react";
import HoverTip from "../../../../components/ui/HoverTip";
import { useGetSystemDailySeriesQuery } from "../../../../store/services/nivApi";
import { formatDayKey, TelemetryTooltip } from "./telemetryChartUtils";

// ============================================
// LABELS
// ============================================

const SMS_SOURCE_LABELS = {
  appointment: "זימון תור",
  reminder: "תזכורת",
  recurringReminder: "תזכורת חוזרת",
  digitalSignature: "חתימה",
  questionnaire: "שאלון",
  therapistCancellationAlert: "ביטול מטפל",
  bookingRequestAlert: "בקשת תור",
  endMeeting: "סיום פגישה",
  paymentSummary: "סיכום תשלום",
  teamInvite: "הזמנת צוות (מערכת)",
};

const AI_FUNCTION_LABELS = {
  generateTreatmentSummary: "סיכום טיפול",
  aiGameSelection: "בחירת משחק",
  aiPatientConsultation: "התייעצות מטופל",
  aiTreatmentConsultation: "התייעצות טיפול",
  generateInitialPatientSummary: "סיכום מטופל ראשוני",
  updatePatientSummary: "עדכון סיכום מטופל",
  rebuildPatientSummary: "בנייה מחדש של סיכום",
  enrichTreatmentSummary: "העשרת סיכום",
  aiTreatmentChat: "צ'אט טיפול",
  generateTreatmentsReportSummary: "דוח טיפולים",
  aiFoodEstimate: "פירוק ארוחה",
};

const OTHER_KEY = "__other";
const OTHER_LABEL = "אחר";
const MAX_STACK_SERIES = 4;

// Series colors — scoped in NivTelemetryTab.css under .ntp-section
const C = {
  primary: "var(--ntp-c1)",
  blue: "var(--ntp-c2)",
  purple: "var(--ntp-c3)",
  amber: "var(--ntp-c4)",
  muted: "var(--ntp-c5)",
};
const STACK_COLORS = [C.primary, C.blue, C.purple, C.amber, C.muted];

const RANGES = [
  { days: 30, label: "30 יום" },
  { days: 90, label: "90 יום" },
];

// ============================================
// HELPERS
// ============================================

const n = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
const fmt = (v) => n(v).toLocaleString("he-IL");
const fmtMoney = (v) => `₪${Math.round(n(v)).toLocaleString("he-IL")}`;

/**
 * Collapse a per-day map series into the top-N keys by total + "אחר".
 * Returns [{ key, label, color }] in stack order.
 */
const pickStackKeys = (rows, mapField, labels) => {
  const totals = {};
  for (const r of rows) {
    for (const [k, v] of Object.entries(r[mapField] || {})) {
      totals[k] = (totals[k] || 0) + n(v);
    }
  }
  const sorted = Object.entries(totals)
    .filter(([, v]) => v > 0)
    .sort((a, b) => b[1] - a[1]);
  const top = sorted.slice(0, MAX_STACK_SERIES).map(([k]) => k);
  const rest = sorted.slice(MAX_STACK_SERIES).map(([k]) => k);
  const keys = top.map((k, i) => ({
    key: k,
    label: labels[k] || k,
    color: STACK_COLORS[i],
  }));
  if (rest.length) {
    keys.push({ key: OTHER_KEY, label: OTHER_LABEL, color: STACK_COLORS[MAX_STACK_SERIES], rest });
  }
  return keys;
};

const stackRow = (map, keys) => {
  const out = {};
  for (const k of keys) {
    if (k.key === OTHER_KEY) {
      out[OTHER_KEY] = k.rest.reduce((sum, rk) => sum + n(map?.[rk]), 0);
    } else {
      out[k.key] = n(map?.[k.key]);
    }
  }
  return out;
};

/** Sum of `field` over the last 7 rows vs the 7 before → { cur, prev, pct } */
const weekOverWeek = (rows, getter) => {
  if (rows.length < 2) return null;
  const last7 = rows.slice(-7);
  const prev7 = rows.slice(-14, -7);
  if (!prev7.length) return null;
  const cur = last7.reduce((s, r) => s + n(getter(r)), 0);
  const prev = prev7.reduce((s, r) => s + n(getter(r)), 0);
  const pct = prev > 0 ? Math.round(((cur - prev) / prev) * 100) : null;
  return { cur, prev, pct };
};

/** Last value vs value 7 rows earlier (for level series) */
const levelDelta = (rows, getter) => {
  if (rows.length < 2) return null;
  const cur = n(getter(rows[rows.length - 1]));
  const prevRow = rows[Math.max(0, rows.length - 8)];
  const prev = n(getter(prevRow));
  const pct = prev > 0 ? Math.round(((cur - prev) / prev) * 100) : null;
  return { cur, prev, pct };
};

const movingAvg = (values, window = 7) =>
  values.map((_, i) => {
    const slice = values.slice(Math.max(0, i - window + 1), i + 1);
    return Math.round((slice.reduce((s, v) => s + v, 0) / slice.length) * 10) / 10;
  });

// ============================================
// SMALL COMPONENTS
// ============================================

const DeltaBadge = ({ delta, suffix = "" }) => {
  if (!delta || delta.pct === null) return null;
  const tone = delta.pct > 0 ? "up" : delta.pct < 0 ? "down" : "flat";
  const sign = delta.pct > 0 ? "+" : "";
  return (
    <span className={`ntp-delta ntp-delta-${tone}`} title="7 הימים האחרונים מול 7 הימים שלפניהם">
      {sign}
      {delta.pct}%{suffix}
    </span>
  );
};

const ChartCard = ({ icon: Icon, title, headline, delta, help, isEmpty, children }) => (
  <section className="ntl-card ntp-card">
    <div className="ntp-card-head">
      <h3 className="ntl-card-title">
        <Icon size={15} />
        {title}
        {help && (
          <HoverTip text={help}>
            <span className="ntp-help" tabIndex={0} role="note" aria-label={help}>
              <Info size={12} />
            </span>
          </HoverTip>
        )}
      </h3>
      {!isEmpty && (
        <div className="ntp-headline">
          <span className="ntp-headline-value">{headline}</span>
          <DeltaBadge delta={delta} />
        </div>
      )}
    </div>
    {isEmpty ? (
      <div className="ntl-empty">אין עדיין נתונים לתקופה זו</div>
    ) : (
      <div className="ntl-chart" dir="ltr">
        {children}
      </div>
    )}
  </section>
);

const axisProps = {
  x: {
    dataKey: "day",
    tickLine: false,
    axisLine: { stroke: "var(--color-border)" },
    tick: { fontSize: 11, fill: "var(--color-text-secondary)" },
    interval: "preserveStartEnd",
    minTickGap: 24,
  },
  y: {
    allowDecimals: false,
    tickLine: false,
    axisLine: false,
    tick: { fontSize: 11, fill: "var(--color-text-light)" },
    width: 40,
    tickFormatter: (v) => (Math.abs(v) >= 1000 ? `${Math.round(v / 100) / 10}k` : v),
  },
};

const CHART_HEIGHT = 200;
const MARGIN = { top: 8, right: 8, left: 4, bottom: 0 };

const legendStyle = { fontSize: 11, direction: "rtl" };

// ============================================
// MAIN
// ============================================

function NivProgressSection({ isAuthorized, overview }) {
  const [days, setDays] = useState(30);

  const { data, isLoading, isError } = useGetSystemDailySeriesQuery(
    { days },
    { skip: !isAuthorized }
  );

  const rows = useMemo(() => (Array.isArray(data) ? data : []), [data]);

  // Active therapists / clinics come from the existing telemetry overview
  const engagementRows = useMemo(() => {
    const series = Array.isArray(overview?.dailySeries) ? overview.dailySeries : [];
    return series.slice(-days);
  }, [overview, days]);

  const model = useMemo(() => {
    const day = (r) => formatDayKey(r.dateKey);
    const d = (r) => r.daily || {};
    const c = (r) => r.counts || {};
    const b = (r) => r.business || {};

    // --- usage (deltas) ---
    const smsKeys = pickStackKeys(rows.map(d), "smsBySource", SMS_SOURCE_LABELS);
    const sms = rows.map((r) => ({
      day: day(r),
      total: n(d(r).smsSentCount),
      ...stackRow(d(r).smsBySource, smsKeys),
    }));

    const aiRows = rows.map((r) => {
      const byFn = { ...(d(r).aiTokensByFunction || {}) };
      delete byFn.aiSupportChat;
      return byFn;
    });
    const aiKeys = pickStackKeys(
      aiRows.map((m) => ({ m })),
      "m",
      AI_FUNCTION_LABELS
    );
    const ai = rows.map((r, i) => ({
      day: day(r),
      total: n(d(r).aiTokensNoLenny),
      ...stackRow(aiRows[i], aiKeys),
    }));

    const lenny = rows.map((r) => ({
      day: day(r),
      tokens: n(d(r).lennyTokens),
      requests: n(d(r).lennyRequests),
    }));

    const video = rows.map((r) => ({
      day: day(r),
      minutes: n(d(r).videoMeetingMinutes),
      sessions: n(d(r).videoMeetingSessions),
    }));

    // --- entities (counts) ---
    const activePatients = rows.map((r) => ({
      day: day(r),
      patients: n(c(r).activePatientsTotal),
    }));
    const newPatients = rows.map((r) => ({ day: day(r), patients: n(c(r).newPatients) }));
    const appointments = rows.map((r) => ({
      day: day(r),
      scheduled: n(c(r).appointmentsScheduled),
      completed: n(c(r).appointmentsCompleted),
    }));
    const treatments = rows.map((r) => ({ day: day(r), docs: n(c(r).treatmentsCreated) }));

    // --- engagement (telemetry overview) ---
    const therapistValues = engagementRows.map((r) => n(r.activeTherapists));
    const therapistAvg = movingAvg(therapistValues);
    const therapists = engagementRows.map((r, i) => ({
      day: formatDayKey(r.dateKey),
      therapists: therapistValues[i],
      avg7: therapistAvg[i],
    }));
    const clinics = engagementRows.map((r) => ({
      day: formatDayKey(r.dateKey),
      clinics: n(r.activeClinics),
    }));

    // --- business ---
    const business = rows.map((r) => ({
      day: day(r),
      paying: n(b(r).payingClinics),
      seats: n(b(r).paidSeats),
      trial: n(b(r).trialClinics),
      mrr: n(b(r).mrr),
    }));

    const docs = rows.map((r) => ({
      day: day(r),
      signatures: n(d(r).digitalSignatureCount),
      questionnaires: n(c(r).questionnaireResponses),
    }));

    const last = rows[rows.length - 1];
    const lastEng = engagementRows[engagementRows.length - 1];

    return {
      sms,
      smsKeys,
      smsTotal: sms.reduce((s, r) => s + r.total, 0),
      smsDelta: weekOverWeek(rows, (r) => d(r).smsSentCount),

      ai,
      aiKeys,
      aiTotal: ai.reduce((s, r) => s + r.total, 0),
      aiDelta: weekOverWeek(rows, (r) => d(r).aiTokensNoLenny),

      lenny,
      lennyTotal: lenny.reduce((s, r) => s + r.tokens, 0),
      lennyRequestsTotal: lenny.reduce((s, r) => s + r.requests, 0),
      lennyDelta: weekOverWeek(rows, (r) => d(r).lennyTokens),

      video,
      videoTotal: video.reduce((s, r) => s + r.minutes, 0),
      videoDelta: weekOverWeek(rows, (r) => d(r).videoMeetingMinutes),

      activePatients,
      activePatientsNow: last ? n(c(last).activePatientsTotal) : 0,
      activePatientsDelta: levelDelta(rows, (r) => c(r).activePatientsTotal),

      newPatients,
      newPatientsTotal: newPatients.reduce((s, r) => s + r.patients, 0),
      newPatientsDelta: weekOverWeek(rows, (r) => c(r).newPatients),

      appointments,
      appointmentsTotal: appointments.reduce((s, r) => s + r.scheduled, 0),
      appointmentsDelta: weekOverWeek(rows, (r) => c(r).appointmentsScheduled),

      treatments,
      treatmentsTotal: treatments.reduce((s, r) => s + r.docs, 0),
      treatmentsDelta: weekOverWeek(rows, (r) => c(r).treatmentsCreated),

      therapists,
      therapistsNow: lastEng ? n(lastEng.activeTherapists) : 0,
      therapistsDelta: weekOverWeek(engagementRows, (r) => r.activeTherapists),

      clinics,
      clinicsNow: lastEng ? n(lastEng.activeClinics) : 0,
      clinicsDelta: weekOverWeek(engagementRows, (r) => r.activeClinics),

      business,
      payingNow: last ? n(b(last).payingClinics) : 0,
      seatsNow: last ? n(b(last).paidSeats) : 0,
      mrrNow: last ? n(b(last).mrr) : 0,
      mrrDelta: levelDelta(rows, (r) => b(r).mrr),

      docs,
      signaturesTotal: docs.reduce((s, r) => s + r.signatures, 0),
      questionnairesTotal: docs.reduce((s, r) => s + r.questionnaires, 0),
      docsDelta: weekOverWeek(rows, (r) => n(d(r).digitalSignatureCount) + n(c(r).questionnaireResponses)),
    };
  }, [rows, engagementRows]);

  const noSnapshots = rows.length === 0;
  const noEngagement = engagementRows.length === 0;
  const lastErrors = rows.length ? n(rows[rows.length - 1].countErrors) : 0;

  return (
    <section className="ntp-section">
      <div className="ntp-head">
        <div className="ntp-head-text">
          <h3 className="ntp-title">
            <TrendUp size={16} />
            התקדמות לפי ימים
          </h3>
          <span className="ntl-note">
            <Info size={12} />
            צילום לילי של כל המערכת · שימוש (SMS / AI / וידאו) מחושב כהפרש יומי מהמונים החודשיים
            {lastErrors > 0 && ` · ${lastErrors} קליניקות לא נספרו אמש`}
          </span>
        </div>
        <div className="ntp-range" role="group" aria-label="טווח ימים">
          {RANGES.map((r) => (
            <button
              key={r.days}
              type="button"
              className={`ntp-range-chip${days === r.days ? " is-active" : ""}`}
              onClick={() => setDays(r.days)}
              aria-pressed={days === r.days}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {isError && (
        <div className="ntl-empty">לא הצלחנו לטעון את נתוני ההתקדמות. נסו לרענן את העמוד.</div>
      )}

      {!isError && noSnapshots && !isLoading && (
        <div className="ntl-empty ntp-empty-hint">
          הנתונים מצטברים מהלילה הראשון לאחר ההפעלה. לחצו "חשב מחדש" כדי ליצור נקודה להיום.
        </div>
      )}

      <div className="ntl-charts-grid ntp-grid">
        {/* 1. SMS */}
        <ChartCard
          icon={ChatText}
          title="הודעות SMS ביום"
          headline={`${fmt(model.smsTotal)} בתקופה`}
          delta={model.smsDelta}
          help="הפרש יומי של smsSentCount מכל הקליניקות, מפולח לפי מקור השליחה."
          isEmpty={noSnapshots}
        >
          <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
            <BarChart data={model.sms} margin={MARGIN}>
              <CartesianGrid vertical={false} stroke="var(--color-border-light)" />
              <XAxis {...axisProps.x} />
              <YAxis {...axisProps.y} />
              <Tooltip content={<TelemetryTooltip />} cursor={{ fill: "var(--color-background-alt)" }} />
              <Legend wrapperStyle={legendStyle} iconType="square" iconSize={8} />
              {model.smsKeys.map((k) => (
                <Bar key={k.key} dataKey={k.key} name={k.label} stackId="sms" fill={k.color} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* 2. AI tokens (no Lenny) */}
        <ChartCard
          icon={Sparkle}
          title="טוקנים AI ביום (ללא לני)"
          headline={`${fmt(model.aiTotal)} בתקופה`}
          delta={model.aiDelta}
          help="הפרש יומי של aiUsageByFunction לכל פונקציות ה-AI, למעט צ'אט התמיכה של לני."
          isEmpty={noSnapshots}
        >
          <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
            <BarChart data={model.ai} margin={MARGIN}>
              <CartesianGrid vertical={false} stroke="var(--color-border-light)" />
              <XAxis {...axisProps.x} />
              <YAxis {...axisProps.y} />
              <Tooltip content={<TelemetryTooltip />} cursor={{ fill: "var(--color-background-alt)" }} />
              <Legend wrapperStyle={legendStyle} iconType="square" iconSize={8} />
              {model.aiKeys.map((k) => (
                <Bar key={k.key} dataKey={k.key} name={k.label} stackId="ai" fill={k.color} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* 3. Lenny */}
        <ChartCard
          icon={Robot}
          title="לני — טוקנים ובקשות ביום"
          headline={`${fmt(model.lennyTotal)} טוקנים · ${fmt(model.lennyRequestsTotal)} בקשות`}
          delta={model.lennyDelta}
          help="צ'אט התמיכה (aiSupportChat) בלבד. הפרש יומי של טוקנים ובקשות."
          isEmpty={noSnapshots}
        >
          <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
            <ComposedChart data={model.lenny} margin={MARGIN}>
              <CartesianGrid vertical={false} stroke="var(--color-border-light)" />
              <XAxis {...axisProps.x} />
              <YAxis yAxisId="left" {...axisProps.y} />
              <YAxis yAxisId="right" orientation="right" {...axisProps.y} />
              <Tooltip content={<TelemetryTooltip />} cursor={{ stroke: "var(--color-border)" }} />
              <Legend wrapperStyle={legendStyle} iconType="square" iconSize={8} />
              <Area
                yAxisId="left"
                type="monotone"
                dataKey="tokens"
                name="טוקנים"
                stroke={C.purple}
                strokeWidth={2}
                fill={C.purple}
                fillOpacity={0.14}
              />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="requests"
                name="בקשות"
                stroke={C.blue}
                strokeWidth={2}
                dot={false}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* 4. Video */}
        <ChartCard
          icon={VideoCamera}
          title="דקות וידאו ביום"
          headline={`${fmt(model.videoTotal)} דקות בתקופה`}
          delta={model.videoDelta}
          help="הפרש יומי של videoMeetingMinutes (מדידת 100ms). מספר הפגישות בטולטיפ."
          isEmpty={noSnapshots}
        >
          <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
            <ComposedChart data={model.video} margin={MARGIN}>
              <CartesianGrid vertical={false} stroke="var(--color-border-light)" />
              <XAxis {...axisProps.x} />
              <YAxis {...axisProps.y} />
              <Tooltip content={<TelemetryTooltip />} cursor={{ stroke: "var(--color-border)" }} />
              <Area
                type="monotone"
                dataKey="minutes"
                name="דקות"
                stroke={C.primary}
                strokeWidth={2}
                fill={C.primary}
                fillOpacity={0.18}
              />
              <Line type="monotone" dataKey="sessions" name="פגישות" stroke={C.blue} strokeWidth={1.5} dot={false} />
            </ComposedChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* 5. Active patients (level) */}
        <ChartCard
          icon={UsersThree}
          title="מטופלים פעילים במערכת"
          headline={fmt(model.activePatientsNow)}
          delta={model.activePatientsDelta}
          help="סך המטופלים שאינם מסומנים כלא-פעילים, בכל הקליניקות, נכון לצילום הלילי."
          isEmpty={noSnapshots}
        >
          <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
            <LineChart data={model.activePatients} margin={MARGIN}>
              <CartesianGrid vertical={false} stroke="var(--color-border-light)" />
              <XAxis {...axisProps.x} />
              <YAxis {...axisProps.y} domain={["auto", "auto"]} />
              <Tooltip content={<TelemetryTooltip />} cursor={{ stroke: "var(--color-border)" }} />
              <Line
                type="monotone"
                dataKey="patients"
                name="מטופלים פעילים"
                stroke={C.primary}
                strokeWidth={2.5}
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* 6. New patients */}
        <ChartCard
          icon={UserPlus}
          title="מטופלים חדשים ביום"
          headline={`${fmt(model.newPatientsTotal)} בתקופה`}
          delta={model.newPatientsDelta}
          help="כרטיסי מטופל שנוצרו באותו יום (לפי createdAt), בכל הקליניקות."
          isEmpty={noSnapshots}
        >
          <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
            <BarChart data={model.newPatients} margin={MARGIN}>
              <CartesianGrid vertical={false} stroke="var(--color-border-light)" />
              <XAxis {...axisProps.x} />
              <YAxis {...axisProps.y} />
              <Tooltip content={<TelemetryTooltip />} cursor={{ fill: "var(--color-background-alt)" }} />
              <Bar dataKey="patients" name="מטופלים חדשים" fill={C.blue} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* 7. Appointments */}
        <ChartCard
          icon={CalendarCheck}
          title="תורים ביום"
          headline={`${fmt(model.appointmentsTotal)} בתקופה`}
          delta={model.appointmentsDelta}
          help="תורים שמועדם באותו יום (startTime) וכמה מהם הושלמו."
          isEmpty={noSnapshots}
        >
          <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
            <BarChart data={model.appointments} margin={MARGIN} barGap={-14}>
              <CartesianGrid vertical={false} stroke="var(--color-border-light)" />
              <XAxis {...axisProps.x} />
              <YAxis {...axisProps.y} />
              <Tooltip content={<TelemetryTooltip />} cursor={{ fill: "var(--color-background-alt)" }} />
              <Legend wrapperStyle={legendStyle} iconType="square" iconSize={8} />
              <Bar dataKey="scheduled" name="נקבעו" fill={C.muted} />
              <Bar dataKey="completed" name="הושלמו" fill={C.primary} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* 8. Treatment docs */}
        <ChartCard
          icon={NotePencil}
          title="תיעודי טיפול ביום"
          headline={`${fmt(model.treatmentsTotal)} בתקופה`}
          delta={model.treatmentsDelta}
          help="מסמכי טיפול שנוצרו באותו יום (כולל טיוטות), בכל הקליניקות."
          isEmpty={noSnapshots}
        >
          <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
            <BarChart data={model.treatments} margin={MARGIN}>
              <CartesianGrid vertical={false} stroke="var(--color-border-light)" />
              <XAxis {...axisProps.x} />
              <YAxis {...axisProps.y} />
              <Tooltip content={<TelemetryTooltip />} cursor={{ fill: "var(--color-background-alt)" }} />
              <Bar dataKey="docs" name="תיעודים" fill={C.purple} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* 9. Active therapists */}
        <ChartCard
          icon={ChartLineUp}
          title="מטפלים פעילים ביום"
          headline={`${fmt(model.therapistsNow)} אתמול`}
          delta={model.therapistsDelta}
          help="מטפלים עם לפחות כניסה אחת באותו יום (טלמטריה). הקו המקווקו: ממוצע נע של 7 ימים."
          isEmpty={noEngagement}
        >
          <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
            <ComposedChart data={model.therapists} margin={MARGIN}>
              <CartesianGrid vertical={false} stroke="var(--color-border-light)" />
              <XAxis {...axisProps.x} />
              <YAxis {...axisProps.y} />
              <Tooltip content={<TelemetryTooltip />} cursor={{ stroke: "var(--color-border)" }} />
              <Area
                type="monotone"
                dataKey="therapists"
                name="מטפלים פעילים"
                stroke={C.primary}
                strokeWidth={2}
                fill={C.primary}
                fillOpacity={0.18}
              />
              <Line
                type="monotone"
                dataKey="avg7"
                name="ממוצע 7 ימים"
                stroke={C.blue}
                strokeWidth={1.5}
                strokeDasharray="4 3"
                dot={false}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* 10. Active clinics */}
        <ChartCard
          icon={Buildings}
          title="קליניקות פעילות ביום"
          headline={`${fmt(model.clinicsNow)} אתמול`}
          delta={model.clinicsDelta}
          help="קליניקות עם לפחות כניסה אחת של מטפל באותו יום."
          isEmpty={noEngagement}
        >
          <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
            <ComposedChart data={model.clinics} margin={MARGIN}>
              <CartesianGrid vertical={false} stroke="var(--color-border-light)" />
              <XAxis {...axisProps.x} />
              <YAxis {...axisProps.y} />
              <Tooltip content={<TelemetryTooltip />} cursor={{ stroke: "var(--color-border)" }} />
              <Area
                type="monotone"
                dataKey="clinics"
                name="קליניקות פעילות"
                stroke={C.blue}
                strokeWidth={2}
                fill={C.blue}
                fillOpacity={0.14}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* 11. Business */}
        <ChartCard
          icon={CurrencyDollar}
          title="קליניקות משלמות · מושבים · MRR"
          headline={`${fmt(model.payingNow)} משלמות · ${fmt(model.seatsNow)} מושבים · ${fmtMoney(model.mrrNow)}`}
          delta={model.mrrDelta}
          help="צילום יומי מה-CRM: קליניקות בסטטוס משלם, סך מושבים ששולמו, קליניקות בתוכנית ניסיון, ו-MRR לפני מע״מ (חד-פעמי פרוס על חודשי העסקה)."
          isEmpty={noSnapshots}
        >
          <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
            <LineChart data={model.business} margin={MARGIN}>
              <CartesianGrid vertical={false} stroke="var(--color-border-light)" />
              <XAxis {...axisProps.x} />
              <YAxis yAxisId="left" {...axisProps.y} />
              <YAxis yAxisId="right" orientation="right" {...axisProps.y} />
              <Tooltip content={<TelemetryTooltip />} cursor={{ stroke: "var(--color-border)" }} />
              <Legend wrapperStyle={legendStyle} iconType="square" iconSize={8} />
              <Line yAxisId="left" type="monotone" dataKey="paying" name="משלמות" stroke={C.primary} strokeWidth={2} dot={false} />
              <Line yAxisId="left" type="monotone" dataKey="seats" name="מושבים" stroke={C.blue} strokeWidth={2} dot={false} />
              <Line yAxisId="left" type="monotone" dataKey="trial" name="בניסיון" stroke={C.muted} strokeWidth={1.5} strokeDasharray="4 3" dot={false} />
              <Line yAxisId="right" type="monotone" dataKey="mrr" name="MRR ₪" stroke={C.amber} strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* 12. Signatures + questionnaires */}
        <ChartCard
          icon={Signature}
          title="חתימות ושאלונים ביום"
          headline={`${fmt(model.signaturesTotal)} חתימות · ${fmt(model.questionnairesTotal)} שאלונים`}
          delta={model.docsDelta}
          help="חתימות: הפרש יומי של digitalSignatureCount. שאלונים: תשובות שנוצרו באותו יום."
          isEmpty={noSnapshots}
        >
          <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
            <BarChart data={model.docs} margin={MARGIN}>
              <CartesianGrid vertical={false} stroke="var(--color-border-light)" />
              <XAxis {...axisProps.x} />
              <YAxis {...axisProps.y} />
              <Tooltip content={<TelemetryTooltip />} cursor={{ fill: "var(--color-background-alt)" }} />
              <Legend wrapperStyle={legendStyle} iconType="square" iconSize={8} />
              <Bar dataKey="signatures" name="חתימות" fill={C.primary} />
              <Bar dataKey="questionnaires" name="שאלונים" fill={C.purple} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>
    </section>
  );
}

export default NivProgressSection;
